import { Component, TemplateRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Apollo } from 'apollo-angular';
import { forkJoin, Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { HeaderComponent } from '../../header/header.component';
import { NavComponent } from '../../navigation/nav.component';
import { DashboardService } from '@services/dashboard.service';
import { CREATE_TAXI_FLEET_ACCOUNT } from '@gql_queries/taxi_fleet/mutations';
import { FleetDocType } from '@enums/fleet_doc_type_enums';

const FLEET_DOCUMENT_UPLOAD_DIRECTORY = 'fleet_documents';

interface FleetDocumentUrl {
  type: FleetDocType;
  url: string;
}

interface EnrollmentForm {
  fleetType: string;
  activeVehicles: string;
  operatorName: string;
  registeredBusinessName: string;
  repFirstName: string;
  repLastName: string;
  repEmail: string;
  repMobile: string;
  ltfrbCpcNumber: string;
  cpcExpirationDate: string;
  adminFirstName: string;
  adminLastName: string;
  adminEmail: string;
  adminMobile: string;
  adminRole: string;
}

interface EnrollmentPayload {
  input: {
    operatorName: string;
    registeredBusinessName: string;
    representativeFirstName: string;
    representativeLastName: string;
    representativeEmail: string | null;
    representativeMobile: string;
    ltfrbCpcNumber: string | null;
    fleetType: string;
    cpcExpirationDate: string | null;
    documents: { documentUrl: string; documentType: FleetDocType }[];
    allowedActiveVehicles: number;
    adminFirstName: string;
    adminLastName: string;
    adminEmail: string;
    adminMobile: string;
  };
}

interface FieldErrors {
  fleetType?: string;
  activeVehicles?: string;
  operatorName?: string;
  registeredBusinessName?: string;
  repFirstName?: string;
  repLastName?: string;
  repEmail?: string;
  repMobile?: string;
  validId?: string;
  ltfrbCpcNumber?: string;
  cpcExpirationDate?: string;
  adminFirstName?: string;
  adminLastName?: string;
  adminEmail?: string;
  adminMobile?: string;
}

const ACCEPTED_FILE_TYPES = [
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/svg+xml',
  'application/pdf',
  'application/msword',
];

const PH_MOBILE_REGEX = /^(09\d{9}|639\d{9})$/;
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const DUPLICATE_OPERATOR_MESSAGE =
  'Taxi Fleet Operator already exists. Please check and try again.';

const MOBILE_REQUIRED_MESSAGE = 'Enter Mobile Number';
const MOBILE_INVALID_MESSAGE = 'Please enter a PH mobile number';

const DEFAULT_FORM_LEVEL_MESSAGE = 'Please complete the required fields.';
const MOBILE_FORM_LEVEL_MESSAGE =
  'Please enter a valid mobile number. Format: 09XXXXXXXXX / 639XXXXXXXXX.';
const MOBILE_AND_FIELDS_FORM_LEVEL_MESSAGE =
  'Please complete the required fields and enter valid mobile numbers (09XXXXXXXXX / 639XXXXXXXXX).';

@Component({
  selector: 'app-taxi-fleet-create',
  standalone: true,
  imports: [HeaderComponent, NavComponent, CommonModule, FormsModule, RouterModule],
  templateUrl: './taxi-fleet-create.component.html',
  styleUrl: './taxi-fleet-create.component.css',
})
export class TaxiFleetCreateComponent {
  readonly fleetTypeOptions = [
    { value: 'taxi', label: 'Taxi' },
    { value: 'tnvs', label: 'TNVS' },
    { value: 'truck', label: 'Truck' },
  ];

  form: EnrollmentForm = {
    fleetType: 'taxi',
    activeVehicles: '',
    operatorName: '',
    registeredBusinessName: '',
    repFirstName: '',
    repLastName: '',
    repEmail: '',
    repMobile: '',
    ltfrbCpcNumber: '',
    cpcExpirationDate: '',
    adminFirstName: '',
    adminLastName: '',
    adminEmail: '',
    adminMobile: '',
    adminRole: 'Admin',
  };

  fieldErrors: FieldErrors = {};
  showFormLevelError = false;
  formLevelErrorMessage = DEFAULT_FORM_LEVEL_MESSAGE;
  isSubmitting = false;
  errorMessage = 'Unable to complete enrollment. Please check the details and try again.';

  validIdFile: File | null = null;
  validIdPreviewUrl = '';
  showValidIdThumb = false;

  get requiresCpcDocument(): boolean {
    return this.form.fleetType === 'taxi' || this.form.fleetType === 'truck';
  }

  get isCpcFieldsDisabled(): boolean {
    return this.form.fleetType === 'tnvs';
  }

  onFleetTypeChange(): void {
    if (this.isCpcFieldsDisabled) {
      this.form.ltfrbCpcNumber = '';
      this.form.cpcExpirationDate = '';
    }
  }

  constructor(
    private router: Router,
    private modalService: NgbModal,
    private apollo: Apollo,
    private dasher: DashboardService
  ) {}

  onActiveVehiclesInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const digits = input.value.replace(/\D/g, '').slice(0, 4);
    if (digits !== input.value) {
      input.value = digits;
    }
    this.form.activeVehicles = digits;
  }

  onValidIdChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];

    this.clearValidIdPreview();

    if (!file) {
      return;
    }

    if (!ACCEPTED_FILE_TYPES.includes(file.type)) {
      this.fieldErrors.validId = 'File type invalid. Please check and try again.';
      this.validIdFile = null;
      input.value = '';
      return;
    }

    delete this.fieldErrors.validId;
    this.validIdFile = file;
    this.showValidIdThumb = true;

    if (file.type.startsWith('image/')) {
      this.validIdPreviewUrl = URL.createObjectURL(file);
    } else {
      this.validIdPreviewUrl = '../../../../assets/okito/images/docs.png';
    }
  }

  removeValidId(): void {
    this.clearValidIdPreview();
    const input = document.getElementById('valid-id-file') as HTMLInputElement | null;
    if (input) {
      input.value = '';
    }
  }

  clearValidIdPreview(): void {
    if (this.validIdPreviewUrl.startsWith('blob:')) {
      URL.revokeObjectURL(this.validIdPreviewUrl);
    }
    this.validIdPreviewUrl = '';
    this.validIdFile = null;
    this.showValidIdThumb = false;
  }

  hasFieldError(field: keyof FieldErrors): boolean {
    return !!this.fieldErrors[field];
  }

  private uploadDocument(file: File, type: FleetDocType): Observable<FleetDocumentUrl> {
    const formData = new FormData();
    formData.append('file', file, file.name);
    return this.dasher
      .uploadPhoto(formData, FLEET_DOCUMENT_UPLOAD_DIRECTORY)
      .pipe(map((res: any) => ({ type, url: res.photo })));
  }

  private uploadDocuments(): Observable<FleetDocumentUrl[]> {
    const uploads: Observable<FleetDocumentUrl>[] = [];
    if (this.validIdFile) {
      uploads.push(this.uploadDocument(this.validIdFile, FleetDocType.VALID_ID));
    }

    if (uploads.length === 0) {
      return of([]);
    }

    return forkJoin(uploads);
  }

  getEnrollmentPayload(documentUrls: FleetDocumentUrl[]): EnrollmentPayload {
    return {
      input: {
        operatorName: this.form.operatorName.trim(),
        registeredBusinessName: this.form.registeredBusinessName.trim(),
        representativeFirstName: this.form.repFirstName.trim(),
        representativeLastName: this.form.repLastName.trim(),
        representativeEmail: this.form.repEmail.trim() || null,
        representativeMobile: this.form.repMobile.trim(),
        ltfrbCpcNumber: this.form.ltfrbCpcNumber.trim() || null,
        fleetType: this.form.fleetType,
        cpcExpirationDate: this.form.cpcExpirationDate.trim() || null,
        documents: documentUrls.map((doc) => ({ documentUrl: doc.url, documentType: doc.type })),
        allowedActiveVehicles: parseInt(this.form.activeVehicles, 10),
        adminFirstName: this.form.adminFirstName.trim(),
        adminLastName: this.form.adminLastName.trim(),
        adminEmail: this.form.adminEmail.trim(),
        adminMobile: this.form.adminMobile.trim(),
      },
    };
  }

  getErrorMessage(error: any): string {
    const message =
      error?.errors?.[0]?.message
      || error?.graphQLErrors?.[0]?.message
      || error?.networkError?.error?.errors?.[0]?.message
      || error?.networkError?.result?.errors?.[0]?.message
      || error?.networkError?.message
      || error?.message
      || this.errorMessage;

    // Soft-map backend duplicate/exists errors to OP2-102 copy.
    if (/already exists|duplicate|already registered/i.test(message)) {
      return DUPLICATE_OPERATOR_MESSAGE;
    }

    return message;
  }

  submitEnrollment(successModal: TemplateRef<unknown>, errorModal: TemplateRef<unknown>): void {
    if (this.isSubmitting) {
      return;
    }

    this.fieldErrors = {};
    this.showFormLevelError = false;
    this.formLevelErrorMessage = DEFAULT_FORM_LEVEL_MESSAGE;
    this.errorMessage = 'Unable to complete enrollment. Please check the details and try again.';

    let hasErrors = false;
    let hasMobileErrors = false;

    if (!this.form.fleetType) {
      this.fieldErrors.fleetType = 'Select Fleet Type';
      hasErrors = true;
    }

    if (!this.form.activeVehicles.trim()) {
      this.fieldErrors.activeVehicles = 'Enter number of allowed active vehicles';
      hasErrors = true;
    }

    if (!this.form.operatorName.trim()) {
      this.fieldErrors.operatorName = 'Enter Taxi Fleet Operator Name';
      hasErrors = true;
    }

    if (!this.form.registeredBusinessName.trim()) {
      this.fieldErrors.registeredBusinessName = 'Enter Registered Business Name';
      hasErrors = true;
    }

    if (!this.form.repFirstName.trim()) {
      this.fieldErrors.repFirstName = 'Enter First Name';
      hasErrors = true;
    }

    if (!this.form.repLastName.trim()) {
      this.fieldErrors.repLastName = 'Enter Last Name';
      hasErrors = true;
    }

    if (this.form.repEmail.trim() && !EMAIL_REGEX.test(this.form.repEmail.trim())) {
      this.fieldErrors.repEmail = 'Please enter a valid email address';
      hasErrors = true;
    }

    // Representative mobile is optional; only validate the format when provided.
    if (this.form.repMobile.trim() && !PH_MOBILE_REGEX.test(this.form.repMobile.trim())) {
      this.fieldErrors.repMobile = MOBILE_INVALID_MESSAGE;
      hasMobileErrors = true;
    }

    // OP2-102 marks both CPC fields as required. TNVS fleets have no CPC, so the
    // fields are disabled and skipped for that type. CPC document upload is fleet-side only.
    if (!this.isCpcFieldsDisabled) {
      if (!this.form.ltfrbCpcNumber.trim()) {
        this.fieldErrors.ltfrbCpcNumber = 'Enter Valid LTFRB CPC Number';
        hasErrors = true;
      }

      if (!this.form.cpcExpirationDate.trim()) {
        this.fieldErrors.cpcExpirationDate = 'Enter CPC Expiration Date';
        hasErrors = true;
      }
    }

    if (!this.form.adminFirstName.trim()) {
      this.fieldErrors.adminFirstName = 'Enter Admin First Name';
      hasErrors = true;
    }

    if (!this.form.adminLastName.trim()) {
      this.fieldErrors.adminLastName = 'Enter Admin Last Name';
      hasErrors = true;
    }

    if (!this.form.adminEmail.trim()) {
      this.fieldErrors.adminEmail = 'Enter Email Address';
      hasErrors = true;
    } else if (!EMAIL_REGEX.test(this.form.adminEmail.trim())) {
      this.fieldErrors.adminEmail = 'Please enter a valid email address';
      hasErrors = true;
    }

    if (!this.form.adminMobile.trim()) {
      this.fieldErrors.adminMobile = MOBILE_REQUIRED_MESSAGE;
      hasMobileErrors = true;
    } else if (!PH_MOBILE_REGEX.test(this.form.adminMobile.trim())) {
      this.fieldErrors.adminMobile = MOBILE_INVALID_MESSAGE;
      hasMobileErrors = true;
    }

    if (hasErrors || hasMobileErrors) {
      this.showFormLevelError = true;
      this.formLevelErrorMessage = hasMobileErrors
        ? (hasErrors ? MOBILE_AND_FIELDS_FORM_LEVEL_MESSAGE : MOBILE_FORM_LEVEL_MESSAGE)
        : DEFAULT_FORM_LEVEL_MESSAGE;
      return;
    }

    this.isSubmitting = true;

    this.uploadDocuments().subscribe({
      next: (documentUrls) => {
        const payload = this.getEnrollmentPayload(documentUrls);

        this.apollo.mutate({
          mutation: CREATE_TAXI_FLEET_ACCOUNT,
          variables: payload,
        }).subscribe({
          next: (result: any) => {
            const fleetId = result?.data?.createFleetAccount?.fleetId;

            if (!fleetId) {
              this.isSubmitting = false;
              this.errorMessage = this.getErrorMessage(result);
              this.modalService.open(errorModal, {
                size: 'md',
                backdrop: 'static',
                keyboard: false,
              });
              return;
            }

            this.isSubmitting = false;
            this.modalService.open(successModal, {
              size: 'md',
              backdrop: 'static',
              keyboard: false,
            });
          },
          error: (error) => {
            this.isSubmitting = false;
            this.errorMessage = this.getErrorMessage(error);
            this.modalService.open(errorModal, {
              size: 'md',
              backdrop: 'static',
              keyboard: false,
            });
          },
        });
      },
      error: (error) => {
        this.isSubmitting = false;
        this.errorMessage = 'Unable to upload documents. Please check the files and try again.';
        this.modalService.open(errorModal, {
          size: 'md',
          backdrop: 'static',
          keyboard: false,
        });
      },
    });
  }

  closeSuccessModal(): void {
    this.modalService.dismissAll();
    this.router.navigate(['/admin/taxi-fleet']);
  }
}
